// FE-SCRM-053 — Sales Contact REST client.
//
// Wraps:
// * ``GET    /api/v1/sales/contacts``              (flat list — FE-SCRM-053)
// * ``GET    /api/v1/sales/contacts/{id}``         (detail — BE-SCRM-005)
// * ``PATCH  /api/v1/sales/contacts/{id}``         (update — BE-SCRM-005)
// * ``GET    /api/v1/sales/opportunity-contacts``  (?contact_id= — FE-SCRM-053)
//
// The flat list endpoint is paginated (page / page_size) and accepts
// ?q / ?prospect_id (repeatable) / ?is_primary / ?include_archived.

import { api } from "@/api/client";

import type {
  ContactListQuery,
  ContactListResponse,
  ContactPatchInput,
  ContactRolesResponse,
  SalesContact,
} from "./types";


function _encodeListQuery(query: ContactListQuery): string {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  for (const pid of query.prospect_id ?? []) {
    params.append("prospect_id", pid);
  }
  if (query.is_primary !== undefined) {
    params.set("is_primary", String(query.is_primary));
  }
  if (query.include_archived) params.set("include_archived", "true");
  if (query.page && query.page > 1) {
    params.set("page", String(query.page));
  }
  if (query.page_size && query.page_size !== 50) {
    params.set("page_size", String(query.page_size));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}


export function fetchContactsList(
  query: ContactListQuery,
  opts?: { signal?: AbortSignal },
): Promise<ContactListResponse> {
  return api.get<ContactListResponse>(
    `/api/v1/sales/contacts${_encodeListQuery(query)}`,
    opts,
  );
}


export function fetchContactDetail(
  contact_id: string,
  opts?: { signal?: AbortSignal },
): Promise<SalesContact> {
  return api.get<SalesContact>(
    `/api/v1/sales/contacts/${contact_id}`, opts,
  );
}


export function patchContact(
  contact_id: string, patch: ContactPatchInput,
): Promise<SalesContact> {
  return api.patch<SalesContact>(
    `/api/v1/sales/contacts/${contact_id}`, patch,
  );
}


export function fetchContactRoles(
  contact_id: string,
  opts?: { signal?: AbortSignal },
): Promise<ContactRolesResponse> {
  return api.get<ContactRolesResponse>(
    `/api/v1/sales/opportunity-contacts?contact_id=${contact_id}`,
    opts,
  );
}
